import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, LinearProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import type { input, output } from 'zod';

import type { DialogType } from '../../../../@types';
import type { WelcomeBonusCampaign } from '../../../../@types/api';
import { QUERY_KEYS } from '../../../../constants';
import API_ROUTES from '../../../../constants/apiRoutes';
import { AFFILIATE_CAMPAIGN_STATUS, AFFILIATE_CAMPAIGN_TYPE, VALIDITY_PERIOD } from '../../../../constants/enums';
import useMutateData from '../../../../hooks/useMutateData';
import { useMarketplace } from '../../../../hooks/useMarketplace';
import usePermissions from '../../../../hooks/usePermissions';
import { campaignCreateFormSchema, type CampaignCreateFormData } from '../../../../schema';
import { formatSnakeCaseString } from '../../../../utils';
import { getData } from '../../../../utils/api';
import FormModalLayout from '../../../layouts/FormModalLayout';
import FormSelect from '../../../molecules/FormSelect';
import RetryAlert from '../../../molecules/RetryAlert';
import { WelcomeBonusReview } from '../welcome-bonus/WelcomeBonusReview';
import CampaignFields from './CampaignFields';

type AffiliateWelcomeBonusOptions = {
  items: WelcomeBonusCampaign[];
};

const EMPTY_WELCOME_BONUSES: WelcomeBonusCampaign[] = [];

const getDefaultFormData = (): CampaignCreateFormData => ({
  name: '',
  type: AFFILIATE_CAMPAIGN_TYPE.BANNER,
  code: '',
  welcomeBonusId: '',
  url: undefined,
  validityPeriod: VALIDITY_PERIOD.ONGOING,
  fromTimestamp: undefined,
  toTimestamp: undefined,
  status: AFFILIATE_CAMPAIGN_STATUS.READY,
  description: '',
});

const getWelcomeBonusOptionLabel = (bonus: WelcomeBonusCampaign) => {
  const scope = bonus.welcomeBonusScope ? formatSnakeCaseString(bonus.welcomeBonusScope) : 'Unavailable';

  return `${bonus.name} — ${formatSnakeCaseString(bonus.product)} — ${scope}`;
};

const CreateCampaignForm = ({ closeModal }: DialogType) => {
  const { createDataAsync } = useMutateData(API_ROUTES.CAMPAIGNS, [QUERY_KEYS.campaigns], 'crm');
  const { config: marketplaceConfig } = useMarketplace();
  const { isAdmin, isSuperAdmin } = usePermissions();

  const bonusOptionsQuery = useQuery<AffiliateWelcomeBonusOptions>({
    queryKey: [QUERY_KEYS.affiliateWelcomeBonusOptions],
    queryFn: () => getData(API_ROUTES.WELCOME_BONUS_AFFILIATE_OPTIONS, {}, 'crm'),
    refetchOnMount: 'always',
  });

  const form = useForm<input<typeof campaignCreateFormSchema>, unknown, output<typeof campaignCreateFormSchema>>({
    defaultValues: getDefaultFormData(),
    resolver: zodResolver(campaignCreateFormSchema),
  });
  const {
    control,
    getValues,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = form;

  const welcomeBonusId = useWatch({ control, name: 'welcomeBonusId' });

  const availableBonuses = bonusOptionsQuery.data?.items ?? EMPTY_WELCOME_BONUSES;
  const selectedWelcomeBonus = availableBonuses.find((bonus) => bonus.id === welcomeBonusId);
  const bonusOptions = availableBonuses.map((bonus) => ({
    id: bonus.id,
    name: getWelcomeBonusOptionLabel(bonus),
  }));

  const isBonusOptionsLoading = bonusOptionsQuery.isFetching;
  const isBonusOptionsError = bonusOptionsQuery.isError;
  const hasAvailableBonuses = availableBonuses.length > 0;

  const disableSubmit =
    isAdmin ||
    isSuperAdmin ||
    isBonusOptionsLoading ||
    isBonusOptionsError ||
    !hasAvailableBonuses ||
    !selectedWelcomeBonus;

  const marketplaceBaseUrl = marketplaceConfig?.domain ? `https://${marketplaceConfig.domain}` : null;

  useEffect(() => {
    const currentUrl = getValues('url');

    if (!currentUrl && marketplaceBaseUrl) {
      setValue('url', marketplaceBaseUrl, { shouldDirty: false, shouldTouch: false });
    }
  }, [getValues, marketplaceBaseUrl, setValue]);

  const handleClose = () => {
    closeModal?.();
    reset(getDefaultFormData());
  };

  const handleRetryBonusOptions = () => {
    void bonusOptionsQuery.refetch();
  };

  const onFormSubmit = async (data: CampaignCreateFormData) => {
    if (disableSubmit) return;

    try {
      await createDataAsync(campaignCreateFormSchema.parse(data), handleClose);
    } catch {
      await bonusOptionsQuery.refetch();
    }
  };

  return (
    <FormProvider {...form}>
      <FormModalLayout
        onSave={handleSubmit(onFormSubmit)}
        label='Create campaign'
        onClose={handleClose}
        isEdit={false}
        showEditTabs={false}
        disableSubmit={disableSubmit}
      >
        <CampaignFields mode='create'>
          {isBonusOptionsLoading && <LinearProgress aria-label='Loading available bonuses' />}
          <FormSelect
            control={control}
            name='welcomeBonusId'
            label='Bonus'
            options={bonusOptions}
            error={errors.welcomeBonusId}
            required
            allowDeselect={false}
            disabled={isBonusOptionsLoading || isBonusOptionsError || !hasAvailableBonuses}
          />
          {isBonusOptionsError && (
            <RetryAlert onRetry={handleRetryBonusOptions}>Unable to load available bonuses.</RetryAlert>
          )}
          {!isBonusOptionsLoading && !isBonusOptionsError && !hasAvailableBonuses && (
            <Alert severity='warning'>No bonuses available.</Alert>
          )}
          {selectedWelcomeBonus && <WelcomeBonusReview campaign={selectedWelcomeBonus} />}
        </CampaignFields>
      </FormModalLayout>
    </FormProvider>
  );
};

export default CreateCampaignForm;
